import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Pattern, renderPatternToSceneOptimized } from '@av-platform/rendering-core';

interface PatternPreviewProps {
  pattern: Pattern;
  patternScale?: number;
  rotation?: number;
  color?: string;
}

const PatternPreview = ({
  pattern,
  patternScale = 1,
  rotation = 0,
  color,
}: PatternPreviewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);

  const solid = !pattern || pattern.lines.length === 0;

  useEffect(() => {
    if (solid) return;
    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerWidth = container.clientWidth || 160;
    const containerHeight = container.clientHeight || 160;

    const camera = new THREE.OrthographicCamera(
      -containerWidth / 2,
      containerWidth / 2,
      containerHeight / 2,
      -containerHeight / 2,
      0.1,
      2000
    );
    camera.position.set(0, 0, 100);
    camera.lookAt(0, 0, 0);

    const clipBounds = Math.max(containerWidth, containerHeight);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerWidth, containerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.innerHTML = '';
    container.appendChild(renderer.domElement);

    renderPatternToSceneOptimized(scene, pattern, {
      patternScale,
      rotation,
      color,
      clipBounds,
    });

    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      if (newWidth <= 0 || newHeight <= 0) return;
      camera.left = -newWidth / 2;
      camera.right = newWidth / 2;
      camera.top = newHeight / 2;
      camera.bottom = -newHeight / 2;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      resizeObserver.disconnect();
      scene.traverse((obj: any) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach((m: any) => m.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
      renderer.dispose();
    };
  }, [pattern, patternScale, rotation, color, solid]);

  if (solid) {
    return <div style={{ width: '100%', height: '100%', backgroundColor: color || '#000000' }} />;
  }

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}
    />
  );
};

export default PatternPreview;
